import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import { captureMessage } from 'error_tracker';
import {
	cdnify,
	getAltImage,
	getFeaturedImage,
} from 'core/utils';
import {
	ListingBoxImg,
	ListingBoxAltImg,
} from './styles';

const getImageDimensions = (aspectRatio, height) => ({
	// 240 is the fallback width here bc its the min-width in CSS
	// Establish an aspect ratio here via: https://web.dev/optimize-cls/#modern-best-practice
	height: height,
	width: aspectRatio ? (height / aspectRatio) : 240,
});

class ListingBoxImage extends PureComponent {
	render() {
		const { listingImages, listingName, href, lazy, mobile, height, style, compact } = this.props;
		const { thumbnail, image600, description, aspectRatio } = getFeaturedImage({ listingImages });
		const featuredImgUrl = thumbnail || image600;

		const featuredImgAltText = description || `${listingName} Photo 1`;

		// There *really* should be an image — but if not we have problems so bail:
		if (!featuredImgUrl) {
			captureMessage(`Missing featured listing image for ${href}`);
			return null;
		}

		const imgProps = !lazy ? { src: cdnify(featuredImgUrl) } : {
			className: "lazyload",
			"data-src": cdnify(featuredImgUrl),
		};
		// Put width & height on images to avoid layout shifts
		const dims = getImageDimensions(aspectRatio, height);
		const commonImgProps = {
			$compact: compact,
		};
		const listingImgProps = {
			...imgProps,
			...dims,
			style: style,
			alt: featuredImgAltText,
			title: featuredImgAltText,
		};

		// Only use the slider if there are 3+ images
		if (mobile && listingImages.length >= 3) {
			// Lazy-loading fallback is the singular image
			const ListingSlider = loadable(() => import(
				'core/components/ListingSlider'
			), {
				fallback: <ListingBoxImg {...commonImgProps} {...listingImgProps} />
			});
			return (
				<ListingSlider
					images={listingImages}
					listingName={listingName}
					href={href}
					lazy={lazy}
					height={height}
					style={this.props.style}
				/>
			);
		}

		const altImg = getAltImage({ listingImages });
		const altImgUrl = altImg && (altImg.thumbnail || altImg.image600 || altImg.image);
		const altDims = getImageDimensions(altImg?.aspectRatio, height);

		return (
			<Fragment>
				<ListingBoxImg {...commonImgProps} {...listingImgProps} />
				{/* Always lazyload the alt image since it's behind the featured image */}
				{altImgUrl && (
					<ListingBoxAltImg
						className="lazyload"
						data-src={cdnify(altImgUrl)}
						alt={featuredImgAltText}
						title={featuredImgAltText}
						style={style}
						{...commonImgProps}
						{...altDims}
					/>
				)}
			</Fragment>
		);
	}
}

ListingBoxImage.propTypes = {
	listingImages: PropTypes.array.isRequired,
	href: PropTypes.string.isRequired,
	listingName: PropTypes.string,
	lazy: PropTypes.bool,
	mobile: PropTypes.bool,
	height: PropTypes.number,
	style: PropTypes.string,
	compact: PropTypes.bool,
};

ListingBoxImage.defaultProps = {
	listingImages: [],
	height: 226,
};

export default ListingBoxImage;
